import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useRef, useState } from "react"
import { FiArrowRight, FiBell, FiChevronRight, FiX } from "react-icons/fi"
import {
  type NotificationItemDTO,
  NotificationsService,
} from "../../api/notifications"
import { onNotificationPreferencesUpdated } from "../../services/notificationFeedEvents"
import { notificationStreamService } from "../../services/notificationStreamService"

type SystemNotification = {
  id: string
  type: string
  documentID: string
  eventAt: string
  documentNumber: string
  text: string
  readAt?: string | null
}

type ReconciliationContext = {
  racedLiveItems: Map<string, SystemNotification>
}

const navigableTypes = new Set([
  "ZM",
  "ZAP",
  "CRIT",
  "ZZ",
  "PZ",
  "WZ",
  "EXPIRATION",
])

type SystemNotificationsProps = {
  mode?: "round" | "tile"
}

export default function SystemNotifications({
  mode = "round",
}: SystemNotificationsProps) {
  const [items, setItems] = useState<SystemNotification[]>([])
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const firstFetchRef = useRef(true)
  const lastNotifiedIDRef = useRef<string>("")
  const reconciliationContextsRef = useRef<Set<ReconciliationContext>>(
    new Set(),
  )
  const reconciliationGenerationRef = useRef(0)
  const lastAppliedReconciliationGenerationRef = useRef(0)
  const activeRef = useRef(true)
  const badgeBg = useColorModeValue("red.500", "red.300")
  const badgeColor = useColorModeValue("white", "gray.900")
  const bg = "main.accent"
  const iconColor = "ui.light"
  const unreadBg = useColorModeValue("gray.50", "gray.700")
  const itemBorder = useColorModeValue("gray.200", "gray.600")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const cardBg = useColorModeValue("white", "gray.800")
  const tileBg = useColorModeValue("white", "gray.800")
  const tileHoverBg = useColorModeValue("gray.100", "gray.700")
  const tileActiveBg = useColorModeValue("gray.200", "gray.600")
  const tileTextColor = useColorModeValue("gray.800", "gray.100")
  const tileMutedColor = useColorModeValue("gray.600", "gray.300")
  const tileShadow = useColorModeValue("sm", "dark-lg")

  const reconcileItems = useCallback(
    async (showToast = false) => {
      const generation = ++reconciliationGenerationRef.current
      const reconciliation: ReconciliationContext = {
        racedLiveItems: new Map(),
      }
      reconciliationContextsRef.current.add(reconciliation)
      try {
        const data = buildSystemNotifications(
          await NotificationsService.getAll(undefined, 50),
        )
        if (
          !activeRef.current ||
          !isNewerNotificationReconciliation(
            generation,
            lastAppliedReconciliationGenerationRef.current,
          )
        ) {
          return
        }
        lastAppliedReconciliationGenerationRef.current = generation
        setItems((current) =>
          reconcileNotificationItems(
            current,
            data,
            [...reconciliation.racedLiveItems.values()],
            true,
          ),
        )
        if (
          showToast &&
          !firstFetchRef.current &&
          data.length > 0 &&
          !data[0].readAt &&
          data[0].id !== lastNotifiedIDRef.current
        ) {
          lastNotifiedIDRef.current = data[0].id
          toast({
            title: `Nowe powiadomienia: ${data.length}`,
            description: "Kliknij dzwonek, aby otworzyć centrum powiadomień.",
            status: "info",
            duration: 3000,
            isClosable: true,
          })
        }
        if (!showToast && data.length > 0 && !data[0].readAt) {
          lastNotifiedIDRef.current = data[0].id
        }
      } catch {
        if (
          !activeRef.current ||
          !isNewerNotificationReconciliation(
            generation,
            lastAppliedReconciliationGenerationRef.current,
          )
        ) {
          return
        }
        lastAppliedReconciliationGenerationRef.current = generation
        setItems((current) =>
          reconcileNotificationItems(
            current,
            [],
            [...reconciliation.racedLiveItems.values()],
            false,
          ),
        )
      } finally {
        reconciliationContextsRef.current.delete(reconciliation)
        if (generation === lastAppliedReconciliationGenerationRef.current) {
          firstFetchRef.current = false
        }
      }
    },
    [toast],
  )

  useEffect(() => {
    activeRef.current = true

    const unsubscribeStream = notificationStreamService.subscribe((liveDTO) => {
      if (!activeRef.current) return
      const liveItems = buildSystemNotifications([liveDTO])
      if (liveItems.length === 0) return
      const newItem = liveItems[0]

      reconciliationContextsRef.current.forEach((context) => {
        context.racedLiveItems.set(newItem.id, newItem)
      })

      setItems((prev) => {
        const exists = prev.some((item) => item.id === newItem.id)
        if (exists) return prev
        return [newItem, ...prev]
      })

      if (!newItem.readAt && newItem.id !== lastNotifiedIDRef.current) {
        lastNotifiedIDRef.current = newItem.id
        toast({
          title: `Nowe powiadomienie: ${newItem.type}`,
          description: newItem.text || `Dokument: ${newItem.documentNumber}`,
          status: "info",
          duration: 3000,
          isClosable: true,
        })
      }
    })

    const unsubscribeState = notificationStreamService.onStateChange(
      (state) => {
        if (state === "connected" && !firstFetchRef.current) {
          void reconcileItems(true)
        }
      },
    )
    const unsubscribePreferences = onNotificationPreferencesUpdated(() => {
      void reconcileItems()
    })
    notificationStreamService.connect()
    void (async () => {
      await notificationStreamService.waitUntilConnected()
      await reconcileItems()
    })()

    return () => {
      activeRef.current = false
      reconciliationGenerationRef.current += 1
      lastAppliedReconciliationGenerationRef.current =
        reconciliationGenerationRef.current
      reconciliationContextsRef.current.clear()
      unsubscribeStream()
      unsubscribeState()
      unsubscribePreferences()
      notificationStreamService.disconnect()
    }
  }, [reconcileItems, toast])

  const visibleItems = items

  const navigateToDocument = (item: SystemNotification): boolean => {
    if (item.type === "ZM") {
      navigate({
        to: "/WMS/documents/warehouse-orders/$warehouseOrder",
        params: { warehouseOrder: item.documentID },
        search: { tab: "general" },
      })
      return true
    }
    if (item.type === "ZAP") {
      navigate({
        to: "/WMS/documents/zap/$zap",
        params: { zap: item.documentID },
        search: { tab: "general", edit: undefined },
      })
      return true
    }
    if (item.type === "CRIT") {
      navigate({
        to: "/WMS/warehouses/critical-stock-report/$id",
        params: { id: item.documentID },
      })
      return true
    }
    if (item.type === "ZZ") {
      navigate({
        to: "/WMS/documents/purchase-orders/$purchaseOrder",
        params: { purchaseOrder: item.documentID },
        search: { tab: "general", edit: undefined },
      })
      return true
    }
    if (item.type === "PZ") {
      navigate({
        to: "/WMS/documents/ex-receptions/$exReception",
        params: { exReception: item.documentID },
        search: { tab: "general", edit: undefined },
      })
      return true
    }
    if (item.type === "WZ") {
      navigate({
        to: "/WMS/documents/ex-releases/$exRelease",
        params: { exRelease: item.documentID },
        search: { tab: "general", mode: undefined },
      })
      return true
    }
    if (item.type === "EXPIRATION") {
      navigate({
        to: "/WMS/warehouses/expiration-date-report/$id",
        params: { id: item.documentID },
      })
      return true
    }
    return false
  }

  const openLatest = async () => {
    if (visibleItems.length === 0) return
    const latest = visibleItems[0]
    if (!latest.readAt) {
      await NotificationsService.markRead([latest.id])
    }
    onClose()
    navigateToDocument(latest)
    await reconcileItems()
  }

  const unreadCount = visibleItems.filter((item) => !item.readAt).length

  const markAllRead = async () => {
    const latest = visibleItems[0]?.eventAt
    if (!latest) return
    await NotificationsService.markReadAll(new Date(latest).toISOString())
    await reconcileItems()
  }

  const dismissItem = async (id: string) => {
    await NotificationsService.dismiss([id])
    await reconcileItems()
  }

  const clearAll = async () => {
    if (visibleItems.length === 0) return
    await NotificationsService.dismiss(visibleItems.map((item) => item.id))
    await reconcileItems()
  }

  const markItemRead = async (item: SystemNotification) => {
    if (!item.readAt) {
      await NotificationsService.markRead([item.id])
      await reconcileItems()
    }
  }

  const openItemDocument = async (item: SystemNotification) => {
    if (!item.readAt) {
      await NotificationsService.markRead([item.id])
    }
    onClose()
    navigateToDocument(item)
    await reconcileItems()
  }

  const formatEventTime = (value?: string) => {
    if (!value) return "—"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "—"
    return date.toLocaleString()
  }

  return (
    <>
      <Tooltip
        label={
          unreadCount > 0
            ? `Nowe powiadomienia: ${unreadCount}`
            : "Brak nowych powiadomień"
        }
      >
        <Box position="relative">
          {mode === "tile" ? (
            <Button
              aria-label="Powiadomienia systemowe"
              onClick={onOpen}
              h={{ xl: "52px", "2xl": "56px" }}
              minW={{ xl: "60px", "2xl": "72px" }}
              px={{ xl: 2.5, "2xl": 3 }}
              borderRadius="2xl"
              bg={tileBg}
              color={tileTextColor}
              _hover={{ bg: tileHoverBg }}
              _active={{ bg: tileActiveBg }}
              boxShadow={tileShadow}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={0.5}
            >
              <FiBell />
              <Text
                fontSize="xs"
                fontWeight="600"
                lineHeight="1"
                color={tileMutedColor}
              >
                Powiad.
              </Text>
            </Button>
          ) : (
            <IconButton
              aria-label="Powiadomienia systemowe"
              icon={<FiBell />}
              onClick={onOpen}
              size="md"
              fontSize="lg"
              isRound
              bg={bg}
              _hover={{ bg: "#0f4504" }}
              color={iconColor}
            />
          )}
          {unreadCount > 0 && (
            <Box
              position="absolute"
              top="-6px"
              right="-6px"
              minW="18px"
              h="18px"
              px="4px"
              borderRadius="999px"
              bg={badgeBg}
              color={badgeColor}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="10px" lineHeight="1" fontWeight="700">
                {unreadCount > 99 ? "99+" : unreadCount}
              </Text>
            </Box>
          )}
        </Box>
      </Tooltip>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent bg={cardBg}>
          <DrawerCloseButton />
          <DrawerHeader>Powiadomienia systemowe</DrawerHeader>
          <DrawerBody>
            <HStack mb={3} spacing={2}>
              <Box
                flex={1}
                borderWidth="1px"
                borderColor={itemBorder}
                borderRadius="lg"
                px={3}
                py={2}
              >
                <Text color={mutedText} fontSize="xs">
                  Nieprzeczytane
                </Text>
                <Text fontWeight="bold">{unreadCount}</Text>
              </Box>
              <Box
                flex={1}
                borderWidth="1px"
                borderColor={itemBorder}
                borderRadius="lg"
                px={3}
                py={2}
              >
                <Text color={mutedText} fontSize="xs">
                  Wszystkie
                </Text>
                <Text fontWeight="bold">{visibleItems.length}</Text>
              </Box>
            </HStack>
            <HStack justify="space-between" mb={3}>
              <Text fontSize="sm" color={mutedText}>
                Wszystkie powiadomienia ({visibleItems.length})
              </Text>
              <IconButton
                aria-label="Otwórz najnowsze powiadomienie"
                icon={<FiChevronRight />}
                size="sm"
                variant="ghost"
                onClick={openLatest}
              />
            </HStack>
            {visibleItems.length > 0 && (
              <HStack mb={3} spacing={4}>
                <Text
                  as="button"
                  fontSize="sm"
                  color="blue.500"
                  onClick={markAllRead}
                >
                  Oznacz wszystko jako przeczytane
                </Text>
                <Text
                  as="button"
                  fontSize="sm"
                  color="red.500"
                  onClick={clearAll}
                >
                  Wyczyść wszystkie
                </Text>
              </HStack>
            )}
            <VStack spacing={2} align="stretch">
              {visibleItems.map((item) => {
                const isUnread = !item.readAt
                return (
                  <Box
                    key={item.id}
                    borderWidth="1px"
                    borderColor={itemBorder}
                    borderRadius="lg"
                    p={3}
                    bg={isUnread ? unreadBg : "transparent"}
                    cursor="pointer"
                    onClick={() => markItemRead(item)}
                  >
                    <HStack align="start" justify="space-between" spacing={3}>
                      <Box flex={1}>
                        <HStack spacing={2} mb={1}>
                          <Badge
                            colorScheme={
                              item.type === "ZM"
                                ? "blue"
                                : item.type === "ZZ"
                                  ? "purple"
                                  : item.type === "CRIT"
                                    ? "red"
                                    : "orange"
                            }
                            borderRadius="md"
                          >
                            {item.type}
                          </Badge>
                          {isUnread && (
                            <Badge colorScheme="green" borderRadius="md">
                              Nowe
                            </Badge>
                          )}
                        </HStack>
                        <Text
                          fontSize="sm"
                          fontWeight={isUnread ? "700" : "500"}
                        >
                          {item.text}
                        </Text>
                        <Text fontSize="xs" color={mutedText} mt={1}>
                          Dokument: {item.documentNumber}
                        </Text>
                        <Text fontSize="xs" color={mutedText}>
                          Czas: {formatEventTime(item.eventAt)}
                        </Text>
                      </Box>
                      <HStack spacing={1} align="start">
                        {navigableTypes.has(item.type) && (
                          <IconButton
                            aria-label="Przejdź do dokumentu"
                            icon={<FiArrowRight />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={(e) => {
                              e.stopPropagation()
                              openItemDocument(item)
                            }}
                          />
                        )}
                        <IconButton
                          aria-label="Usuń powiadomienie"
                          icon={<FiX />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={(e) => {
                            e.stopPropagation()
                            dismissItem(item.id)
                          }}
                        />
                      </HStack>
                    </HStack>
                  </Box>
                )
              })}
              {visibleItems.length === 0 && (
                <Text fontSize="sm" color={mutedText}>
                  Brak powiadomień.
                </Text>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}

function buildSystemNotifications(
  data: NotificationItemDTO[],
): SystemNotification[] {
  return data
    .filter(
      (item) =>
        navigableTypes.has(item.type) || item.type === "AUTO_REPLENISHMENT",
    )
    .map<SystemNotification>((item) => ({
      id: item.id,
      type: item.type,
      documentID: item.document_id,
      documentNumber: item.document_number,
      eventAt: item.event_at,
      text: item.text,
      readAt: item.read_at,
    }))
    .sort(
      (a, b) => new Date(b.eventAt).getTime() - new Date(a.eventAt).getTime(),
    )
}

function mergeNotificationItems(
  primary: SystemNotification[],
  secondary: SystemNotification[],
): SystemNotification[] {
  const byID = new Map<string, SystemNotification>()
  for (const item of [...primary, ...secondary]) {
    if (!byID.has(item.id)) {
      byID.set(item.id, item)
    }
  }
  return [...byID.values()]
    .sort(
      (a, b) => new Date(b.eventAt).getTime() - new Date(a.eventAt).getTime(),
    )
    .slice(0, 50)
}

export function reconcileNotificationItems(
  current: SystemNotification[],
  server: SystemNotification[],
  racedLive: SystemNotification[],
  succeeded: boolean,
): SystemNotification[] {
  return mergeNotificationItems(succeeded ? server : current, racedLive)
}

export function isNewerNotificationReconciliation(
  generation: number,
  lastAppliedGeneration: number,
): boolean {
  return generation > lastAppliedGeneration
}
